import {
  Box,
  ButtonProps,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../../assets/icons/export.svg";
import { COLORS } from "../../../assets/theme";
import Actions from "../../../components/common/Actions";
import CustomDateRangePicker from "../../../components/common/CustomDateRangePicker";
import CustomDialog from "../../../components/common/CustomDialog";
import CustomLabel from "../../../components/common/CustomLabel";
import SearchForm from "../../../components/common/SearchForm";
import CustomTable from "../../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../../components/ToastNotification";
import {
  fetchProvider,
  useProviderOptionQuery,
} from "../../../services/ProviderService";
import {
  addSenderTranslationApi,
  ConditionValueType,
  deleteSenderTranslationApi,
  editSenderTranslationApi,
  exportSenderTranslation,
  fetchSenderHistory,
  getSenderTranslationById,
  SenderTranslationCreateAllApi,
  SenderTranslationType,
  useSenderTranslationQuery,
} from "../../../services/SenderTranslationService";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../../types/table/interfaces.type";
import { loadOperatorsByCountry } from "../../../utils/commonDataUtils";

import { useQueryClient } from "@tanstack/react-query";
import ConditionSenderFields from "../../../components/ConditionSenderFields";
import CustomCombinedField from "../../../components/CustomCombinedField";
import TestingSection from "../../../components/TestFields";
import {
  fetchCountry,
  useCountriesQuery,
} from "../../../services/CountryService";
import { fetchOperator } from "../../../services/OperatorsService";
import {
  fetchRoutingPlan,
  useRoutingPlansQuery,
} from "../../../services/RoutingPlanService";
import {
  applyAndConditions,
  applyOrConditions,
  applySingleCondition,
} from "../../../utils/conditionUtils";

import History from "../../../components/History";
import RelatedInstructionsDialog from "../../../components/RelatedInstructionsDialog";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../../components/common/AutoComplete";
import { hasJwtPermission } from "../../../services/ServicesHelpers";

const formatSenderCondition = (condition: ConditionValueType) => {
  const value = condition.value?.trim();

  switch (condition.conditionType) {
    case "CONTAINS":
      return value ? `Sender ID contains ${value}` : "Sender ID contains";
    case "EQUAL_TO":
      return value ? `Sender ID equals ${value}` : "Sender ID equals";
    case "IS_DIGIT":
      return "Sender ID is digit";
    case "IS_NOT_DIGIT":
      return "Sender ID is not digit";
    default:
      if (condition.conditionType && value) {
        return `Sender ID ${condition.conditionType} ${value}`;
      }

      if (condition.conditionType) {
        return `Sender ID ${condition.conditionType}`;
      }

      return value ? `Sender ID ${value}` : "Sender ID condition";
  }
};

const formatSenderConditions = (
  conditions?: ConditionValueType[],
  rowLogicalOperator?: string,
) => {
  if (!conditions || conditions.length === 0) {
    return "No Conditions";
  }

  return conditions.reduce((summary, condition, index) => {
    const formattedCondition = formatSenderCondition(condition);

    if (index === 0) {
      return formattedCondition;
    }

    const logicalOperator =
      condition.logicalOperator || rowLogicalOperator || "AND";
    return `${summary} ${logicalOperator} ${formattedCondition}`;
  }, "");
};

const SenderTranslationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<SenderTranslationType & DateRange>
  >({
    search: "",
  });

  const { data: routingPlansOptions = [] } = useRoutingPlansQuery();
  const { data: providersOptions = [] } = useProviderOptionQuery();
  const { data: countriesOptions = [] } = useCountriesQuery();
  const routingPlanOptions = routingPlansOptions;
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<SenderTranslationType & { countryId: number }>
  >({});

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const dialogRef = useRef<SenderTranslationDialogRef>(null);
  const [relatedRow, setRelatedRow] = useState<SenderTranslationType | null>(
    null,
  );

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: SenderTranslationType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      return newSelected;
    });
  };
  const isRowSelected = (row: SenderTranslationType) =>
    selectedRowIds.includes(String(row.id));

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone = false,
  ) => {
    dialogRef.current?.openDialog(mode, id, isClone);
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      routingPlanId: filters.routingPlanId,
      operatorId: filters.operatorId,
      providerId: filters.providerId,
    }));
    setSelectedRowIds([]);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_SENDER_TRANSLATION"),
    },
    onCloneAction: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id), true);
      },
      isDisabled: !hasJwtPermission("POST_SENDER_TRANSLATION"),
    },
    // onDelete: {
    //   handler: async (id: string | number) => {
    //     const stringId = String(id);
    //     console.log(`Delete action triggered for ID: ${stringId}`);
    //     handleOpenDialog("delete", stringId);
    //   },
    //   isDisabled: false,
    // },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_SENDER_TRANSLATIONS_HISTORY"),
    },
    onCompareRelated: {
      tooltip: "Sender Translations on the same route",
      handler: (row: SenderTranslationType) => setRelatedRow(row),
      isDisabled: (row: SenderTranslationType) =>
        !hasJwtPermission("GET_ALL_SENDER_TRANSLATIONS") ||
        !row?.routingPlanId ||
        !row?.operatorId,
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Routing Plan",
      value: filters.routingPlanId || "",
      options: routingPlanOptions,
      clearable: true,
      placeholder: "Select Routing Plan",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedPlan = routingPlanOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          routingPlanId: String(selectedPlan?.value ?? ""),
        }));
      },
    },
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      placeholder: "Select Country",

      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: Number(selectedCountry?.value),
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(operators);
          } catch (error: any) {
            ToastNotification.error(error.message);

            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,

      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          operatorId: Number(selectedOperator?.value),
        }));
      },
    },

    {
      label: "Provider",
      value: filters.providerId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          providerId: String(selectedProvider?.value ?? ""),
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_SENDER_TRANSLATIONS",
    },
  ];

  const fields = useMemo<IField<SenderTranslationType>[]>(
    () => [
      { key: "name", header: "Name" },
      {
        key: "routingPlanId",
        header: "Routing Plan",
        transform: async (t) => {
          if (!t.routingPlanId) return "-";
          try {
            const rp = await queryClient.fetchQuery({
              queryKey: ["routingPlan", t.routingPlanId],
              queryFn: () => fetchRoutingPlan(t.routingPlanId),
            });
            return rp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => {
          if (!t.providerId) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.providerId],
              queryFn: () => fetchProvider(t.providerId),
            });
            return p?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!op?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", op.countryId],
              queryFn: () => fetchCountry(op.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return op?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "conditionValues",
        header: "Condition",
        transform: async (t) =>
          formatSenderConditions(t.conditionValues, t.logicalOperator),
      },
      {
        key: "translatedSenderId",
        header: "Translated Sender ID",
        transform: (t) => {
          const isRandom =
            (t.cacheTtlSeconds ?? 0) > 0 && (t.randomLength ?? 0) > 0;
          return isRandom
            ? `Randomized ${t.randomLength}-digit, starts with: ${t.translatedSenderId}`
            : t.translatedSenderId;
        },
      },
      { key: "effectiveDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [queryClient],
  );

  const providerNameField = useCallback(
    async (providerId?: string) => {
      if (!providerId) return "-";
      try {
        const p = await queryClient.fetchQuery({
          queryKey: ["provider", providerId],
          queryFn: () => fetchProvider(providerId),
        });
        return p?.name ?? "-";
      } catch {
        return "-";
      }
    },
    [queryClient],
  );

  // Routing plan / country / operator are identical for every row of this view,
  // so only the fields that can actually differ are listed.
  const relatedFields = useMemo<IField<SenderTranslationType>[]>(
    () => [
      { key: "name", header: "Name" },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => providerNameField(t.providerId),
      },
      { key: "position", header: "Position" },
      {
        key: "conditionValues",
        header: "Condition",
        transform: async (t) =>
          formatSenderConditions(t.conditionValues, t.logicalOperator),
      },
      { key: "translatedSenderId", header: "Translated Sender ID" },
      { key: "randomLength", header: "Total Length" },
      { key: "cacheTtlSeconds", header: "Number refreshes every" },
      { key: "effectiveDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [providerNameField],
  );

  function refresh() {
    setAppliedFilters((prev) => ({ ...prev }));
    queryClient.invalidateQueries({ queryKey: ["senderTranslation"] });
  }

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
          rpdid="RPD"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Sender Translation List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_SENDER_TRANSLATION",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_SENDER_TRANSLATION",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,

              onClick: async () => {
                try {
                  await exportSenderTranslation(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_SENDER_TRANSLATIONS_CSV",
            },
          ]}
        />

        <CustomTable<SenderTranslationType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useSenderTranslationQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["name", "effectiveDate", "createdBy"]}
        />
      </Box>

      <SenderTranslationDialog
        ref={dialogRef}
        onRefresh={refresh}
        countriesOptions={countriesOptions}
        providersOptions={providersOptions}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        routingPlanOptions={routingPlanOptions}
        loadOperatorsByCountry={loadOperatorsByCountry}
        appliedFilters={appliedFilters}
      />

      <RelatedInstructionsDialog<SenderTranslationType>
        open={!!relatedRow}
        onClose={() => setRelatedRow(null)}
        row={relatedRow}
        title="Sender Translations on the Same Route"
        fields={relatedFields}
        fetchFunc={useSenderTranslationQuery}
      />
    </Box>
  );
};

interface DialogData extends Omit<SenderTranslationType, "operatorId"> {
  operatorId?: number | number[];
  countryId: number;
  logicalOperator?: "AND" | "OR";
}

export interface SenderTranslationDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone?: boolean,
  ) => void;
}

interface SenderTranslationDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  routingPlanOptions: Option[];
  providersOptions: Option[];
  selectedRowIds: string[];
  appliedFilters: Params<SenderTranslationType>;

  onClearSelection: () => void;
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const SenderTranslationDialog = forwardRef<
  SenderTranslationDialogRef,
  SenderTranslationDialogProps
>(
  (
    {
      onRefresh,
      countriesOptions,
      routingPlanOptions,
      providersOptions,
      appliedFilters,

      loadOperatorsByCountry,
      selectedRowIds,
      onClearSelection,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [isCloneMode, setIsCloneMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [dialogData, setDialogData] = useState<Partial<DialogData>>({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [content, setContent] = useState<string>("");
    const [value, setValue] = useState<string>("");
    const [value2, setValue2] = useState("");
    const [isRandomSenderEnabled, setIsRandomSenderEnabled] = useState(false);

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id, isClone = false) => {
        setDialogMode(isClone ? "add" : mode);
        setSelectedId(isClone ? undefined : id);
        setIsCloneMode(isClone);
        openDialog(mode, id, isClone);
      },
    }));

    useEffect(() => {
      const requiredFields = [
        "routingPlanId",
        "countryId",
        "operatorId",
        "providerId",
        "name",
        "conditionValues",
        "position",
      ];

      const isAnyFieldEmpty = requiredFields.some((field) => {
        const value = dialogData[field as keyof typeof dialogData];
        if (Array.isArray(value)) return value.length === 0;
        return value === undefined || value === null || value === "";
      });

      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);
    const resetDialogData = () => {
      setDialogOpen(false);
      setDialogData({
        name: "",
        conditionValues: [{ conditionType: "", value: "" }],
        routingPlanId: "",
        countryId: undefined,
        operatorId: undefined,
        providerId: "",
        position: undefined,
        cacheTtlSeconds: undefined,
        randomLength: undefined,
      });
      setOperatorsByCountry([]);
      setErrors({});
      setIsSaveDisabled(true);
      setIsOperatorDisabled(true);
      setIsCloneMode(false);
      setIsRandomSenderEnabled(false);
      setValue("");
      setValue2("");
      setContent("");
    };

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country.",
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const isRandomMode =
      (dialogData.cacheTtlSeconds ?? 0) > 0 &&
      (dialogData.randomLength ?? 0) > 0;

    const randomFieldSx = {
      "& .MuiOutlinedInput-root": {
        backgroundColor: COLORS.WhiteBackground,
        borderRadius: "12px",
      },
    };

    const validateForm = () => {
      const baseErrors = {
        name: !dialogData.name,
        routingPlanId: !dialogData.routingPlanId,
        operatorId: !dialogData.operatorId,
        providerId: !dialogData.providerId,
        position: !dialogData.position,
      };

      let randomErrors: Record<string, boolean> = {};
      if (isRandomMode) {
        randomErrors = {
          translatedSenderIdLength:
            value2.length === 0 ||
            value2.length >= (dialogData.randomLength ?? 0),
        };
      }

      setIsSaveDisabled(
        Object.values({ ...baseErrors, ...randomErrors }).some(Boolean),
      );
    };

    useEffect(validateForm, [dialogData, value2, isRandomMode]);

    const addSenderTranslation = async (data: Partial<DialogData>) => {
      try {
        const { operatorId, ...rest } = data;

        const cleanedConditionValues = rest.conditionValues?.map(
          (condition, index) => ({
            ...condition,
            logicalOperator:
              index === 0 ? undefined : condition.logicalOperator || undefined,
          }),
        );

        const commonData: Partial<SenderTranslationType> = {
          ...rest,
          type: "SENDER_TRANSLATION",
          isDeleted: false,
          translatedSenderId: value2,
          conditionValues: cleanedConditionValues,
        };

        if (Array.isArray(operatorId) && operatorId.length >= 1) {
          const payloads: Partial<SenderTranslationType>[] = operatorId.map(
            (id, index) => ({
              ...commonData,
              operatorId: id,
              name: data.name ? `${data.name}_${index + 1}` : undefined,
            }),
          );
          await SenderTranslationCreateAllApi(payloads);
        }

        onRefresh();
        resetDialogData();
        ToastNotification.success("Sender added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const editSenderTranslation = async (data: Partial<DialogData>) => {
      try {
        const id = data.id as string;
        if (!id) {
          ToastNotification.warning("Sender ID is missing.");
          return;
        }
        const { operatorId, ...rest } = data;

        console.log(`Edit function called for ID: ${id}`);
        console.log(`Root level logicalOperator:`, rest.logicalOperator);

        rest.conditionValues?.forEach((condition, index) => {
          console.log(`Condition ${index} logical operator:`, {
            conditionType: condition.conditionType,
            value: condition.value,
            logicalOperator: condition.logicalOperator,
            isFirstCondition: index === 0,
          });
        });

        const logicalOperators =
          rest.conditionValues?.map((condition, index) => ({
            index,
            conditionLogicalOperator: condition.logicalOperator,
            rootLogicalOperator: rest.logicalOperator,
            willBePreserved:
              index > 0 &&
              (!!condition.logicalOperator || !!rest.logicalOperator),
          })) || [];

        console.log(
          `Logical operators processing for ID ${id}:`,
          logicalOperators,
        );

        const cleanedConditionValues = rest.conditionValues?.map(
          (condition, index) => ({
            conditionType: condition.conditionType || "",
            value: condition.value ?? "",
            ...(index > 0 && {
              logicalOperator:
                condition.logicalOperator || rest.logicalOperator,
            }),
          }),
        );

        const commonData: Partial<SenderTranslationType> = {
          ...rest,
          type: "SENDER_TRANSLATION",
          isDeleted: false,
          translatedSenderId: value2,
          conditionValues: cleanedConditionValues,
        };

        const payload: Partial<SenderTranslationType> = {
          ...commonData,
          operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
          name: data.name,
        };
        await editSenderTranslationApi(id, payload);

        onRefresh();
        resetDialogData();
        ToastNotification.success("Sender Translation updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const openDialog = async (
      mode: "add" | "edit" | "delete" | "history",
      id?: string,
      isClone = false,
    ) => {
      setDialogMode(isClone ? "add" : mode);

      try {
        if ((mode === "edit" || isClone) && id) {
          const senderData = await getSenderTranslationById(id);

          const processedConditionValues = senderData.conditionValues?.map(
            (
              condition: {
                conditionType: string;
                value: string;
                logicalOperator?: string;
              },
              index: number,
            ) => ({
              conditionType: condition.conditionType || "",
              value: condition.value ?? "",
              ...(index > 0 && {
                logicalOperator:
                  condition.logicalOperator || senderData.logicalOperator,
              }),
            }),
          ) || [{ conditionType: "", value: "" }];

          // A clone keeps every value of the source route, only the name has to
          // be re-entered because it must stay unique.
          let countryId: number | undefined;
          if (senderData.operatorId) {
            const { countryId: selectedCountry } = await fetchOperator(
              senderData.operatorId,
            );
            if (selectedCountry) {
              countryId = Number(selectedCountry);
              setIsOperatorDisabled(true);
              const operators = await loadOperatorsByCountry(countryId);
              setOperatorsByCountry(operators);
              setIsOperatorDisabled(false);
            }
          }
          setValue2(senderData.translatedSenderId ?? "");
          setIsRandomSenderEnabled(
            (senderData.cacheTtlSeconds ?? 0) > 0 &&
              (senderData.randomLength ?? 0) > 0,
          );

          const sourceOperatorId = Array.isArray(senderData.operatorId)
            ? senderData.operatorId[0]
            : senderData.operatorId;

          setDialogData({
            ...senderData,
            ...(isClone ? { id: undefined, name: "" } : {}),
            translatedSenderId: senderData.translatedSenderId,
            countryId,
            conditionValues: processedConditionValues,
            logicalOperator: senderData.logicalOperator,
            operatorId:
              isClone && sourceOperatorId != null
                ? [Number(sourceOperatorId)]
                : sourceOperatorId,
            cacheTtlSeconds: senderData.cacheTtlSeconds ?? undefined,
            randomLength: senderData.randomLength ?? undefined,
          });
        } else {
          setIsRandomSenderEnabled(false);
          setDialogData({
            name: "",
            conditionValues: [{ conditionType: "", value: "" }],
            effectiveDate: new Date(),
          });
        }

        setDialogOpen(true);
      } catch (error: any) {
        console.error(`Error fetching Sender data:`, error);
      }
    };

    const buildEffectiveSender = (matched: boolean): string => {
      if (!matched) return content;
      if (isRandomMode) {
        const prefix = value2;
        const total = dialogData.randomLength ?? 0;
        const remaining = total - prefix.length;
        if (remaining > 0) {
          const randomSuffix = Array.from({ length: remaining }, () =>
            String(Math.floor(Math.random() * 10)),
          ).join("");
          return `${prefix}${randomSuffix}`;
        }
      }
      return value2;
    };

    const handleTestClick = () => {
      if (!dialogData.conditionValues || !dialogData.conditionValues.length) {
        ToastNotification.error(
          "Please add at least one condition for testing.",
        );
        return;
      }

      let updatedContent = content;

      if (dialogData.conditionValues.length === 1) {
        updatedContent = applySingleCondition(
          dialogData.conditionValues[0],
          updatedContent,
          value2,
        );
      } else if (dialogData.conditionValues.length === 2) {
        const [condition1, condition2] = dialogData.conditionValues;
        const logicalOperator = condition2.logicalOperator || "AND";
        updatedContent =
          logicalOperator === "AND"
            ? applyAndConditions(
                condition1,
                condition2,
                updatedContent,
                content,
                value2,
              )
            : applyOrConditions(
                condition1,
                condition2,
                updatedContent,
                content,
                value2,
              );
      }

      const matched = updatedContent !== content;

      if (isRandomMode && matched) {
        const preview = buildEffectiveSender(true);
        setValue(preview);
        ToastNotification.success(
          `Preview generated (illustrative — server caches the actual value for ${dialogData.cacheTtlSeconds}s).`,
        );
      } else {
        setValue(updatedContent);
        ToastNotification.success("Testing completed. Result updated.");
      }
    };

    const deleteSenderTranslation = async () => {
      try {
        await deleteSenderTranslationApi(selectedRowIds);
        ToastNotification.success("Sender Translation deleted successfully!");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting Sender Translation.");
      }
    };

    const handleRemoveCondition = (index: number) => {
      setDialogData((prev) => {
        const updatedConditions = [...(prev.conditionValues || [])];
        updatedConditions.splice(index, 1);
        return { ...prev, conditionValues: updatedConditions };
      });
    };

    const handleAddCondition = (
      conditionType?: string,
      logicalOperator?: "AND" | "OR",
    ) => {
      const finalConditionType = conditionType ?? "CONTAINS";
      const finalLogicalOperator = logicalOperator;

      setDialogData((prev) => ({
        ...prev,
        logicalOperator: finalLogicalOperator,
        conditionValues: [
          ...(prev.conditionValues || []),
          {
            conditionType: finalConditionType,
            value: "",
            ...(prev.conditionValues &&
              prev.conditionValues.length > 0 && {
                logicalOperator: finalLogicalOperator,
              }),
          },
        ],
      }));
    };

    const handleConditionFieldChange = (
      index: number,
      field: string,
      value: string,
    ) => {
      setDialogData((prev) => {
        const updatedConditions = [...(prev.conditionValues || [])];

        updatedConditions[index] = {
          ...updatedConditions[index],
          [field]: value,
        };
        if (field === "logicalOperator") {
          updatedConditions[index].logicalOperator = value as "AND" | "OR";
        }

        return {
          ...prev,
          conditionValues: updatedConditions,
        };
      });
    };

    const handleLogicalOperatorChange = (
      index: number,
      operator: "AND" | "OR",
    ) => {
      console.log(
        `Updating logical operator for condition ${index} to: ${operator}`,
      );
      handleConditionFieldChange(index, "logicalOperator", operator);
    };

    const stringErrors = Object.keys(errors).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key] = errors[key] ? "This field is required" : "";
        return acc;
      },
      {},
    );

    const fieldHistory = useMemo<
      IField<SenderTranslationType & { historyTimestamp: Date }>[]
    >(
      () => [
        { key: "name", header: "Name" },
        {
          key: "routingPlanId",
          header: "Routing Plan",
          transform: async (t) => {
            if (!t.routingPlanId) return "-";
            try {
              const rp = await queryClient.fetchQuery({
                queryKey: ["routingPlan", t.routingPlanId],
                queryFn: () => fetchRoutingPlan(t.routingPlanId),
              });
              return rp?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "providerId",
          header: "Provider",
          transform: async (t) => {
            if (!t.providerId) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.providerId],
                queryFn: () => fetchProvider(t.providerId),
              });
              return p?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Country",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              if (!op?.countryId) return "-";
              const country = await queryClient.fetchQuery({
                queryKey: ["country", op.countryId],
                queryFn: () => fetchCountry(op.countryId),
              });
              return country?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Operator",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              return op?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "conditionValues",
          header: "Condition",
          transform: async (t) =>
            formatSenderConditions(t.conditionValues, t.logicalOperator),
        },
        {
          key: "translatedSenderId",
          header: "Translated Sender ID",
        },
        { key: "effectiveDate", header: "Created Date" },
        { key: "createdBy", header: "User" },
        { key: "historyTimestamp", header: "Date Modified" },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ],
      [queryClient],
    );

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={resetDialogData}
        mode={dialogMode}
        title={
          dialogMode === "history"
            ? "History"
            : dialogMode === "delete"
              ? "Delete Sender Translation"
              : dialogMode === "edit"
                ? "Edit Sender Translation"
                : isCloneMode
                  ? "Clone Sender Translation"
                  : "Add New Sender Translation"
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={async ({ size = 10 }) => {
                        const result = await fetchSenderHistory(
                          String(selectedId),
                          {
                            size,
                          },
                        );
                        return {
                          ...result,
                          content: result.content.map((item) => ({
                            ...item,
                            historyTimestamp:
                              (item as any).historyTimestamp ??
                              new Date(item.effectiveDate ?? Date.now()),
                          })),
                        };
                      }}
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>
                        <p>
                          Are you sure you want to delete this Sender
                          Translation?
                        </p>
                      </Box>
                    ),
                  },
                ]
              : [
                  {
                    type: "text",
                    label: "Sender Translation Name",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.name || "",
                    validateOnChange: true,
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        name: isError,
                      }));
                    },
                    onChange: (value) => {
                      const trimmedValue = String(value);
                      setDialogData((prev) => ({
                        ...prev,
                        name: trimmedValue,
                      }));
                    },
                    placeholder: "Name",
                    error: !!errors.name,
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Routing Plan"
                        value={dialogData.routingPlanId ?? ""}
                        options={routingPlanOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            routingPlanId: String(value),
                          }));
                          validateField("routingPlan", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.routingPlan}
                        placeholder="Select Routing Plan"
                        highlightLabelStar={true}
                        helperText={
                          errors.routingPlan ? "Routing Plan is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Provider"
                        value={dialogData.providerId ?? ""}
                        options={providersOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            providerId: String(value),
                          }));
                          validateField("providerId", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.providerId}
                        placeholder="Select Provider"
                        highlightLabelStar={true}
                        helperText={
                          errors.providerId ? "Provider is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Country"
                        value={dialogData.countryId ?? ""}
                        options={countriesOptions}
                        onChange={handleCountryChange}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.country}
                        placeholder="Select Country"
                        highlightLabelStar={true}
                        helperText={errors.country ? "Country is required" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Operator"
                        multiple={dialogMode === "add"}
                        selectAll={dialogMode === "add"}
                        hideChips={dialogMode === "add"}
                        value={
                          dialogMode === "add"
                            ? Array.isArray(dialogData.operatorId)
                              ? dialogData.operatorId.map(String)
                              : []
                            : dialogData.operatorId !== undefined &&
                                dialogData.operatorId !== null
                              ? String(
                                  Array.isArray(dialogData.operatorId)
                                    ? dialogData.operatorId[0]
                                    : dialogData.operatorId,
                                )
                              : ""
                        }
                        options={operatorsByCountry}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          if (dialogMode === "add") {
                            const valueArray: number[] = Array.isArray(value)
                              ? value.map((v) => Number(v))
                              : value
                                ? [Number(value)]
                                : [];

                            setDialogData((prev) => ({
                              ...prev,
                              operatorId: valueArray,
                            }));
                          } else {
                            setDialogData((prev) => ({
                              ...prev,
                              operatorId:
                                value !== "" && value !== undefined
                                  ? Number(
                                      Array.isArray(value) ? value[0] : value,
                                    )
                                  : undefined,
                            }));
                          }
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.operatorId}
                        placeholder="Select Operator"
                        highlightLabelStar={true}
                        helperText={
                          errors.operatorId ? "Operator is required" : ""
                        }
                        disabled={isOperatorDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "select",
                    label: "Position",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.position || "",
                    onChange: (
                      value: string | number | (string | number)[],
                    ) => {
                      setDialogData((prev) => ({
                        ...prev,
                        position: String(value) as
                          "BEFORE_SUB_ROUTING" | "AFTER_SUB_ROUTING",
                      }));
                      validateField("position", String(value));
                    },
                    options: [
                      {
                        label: "Apply Before Sub & TPS Routing",
                        value: "BEFORE_SUB_ROUTING",
                      },
                      {
                        label: "Apply After Sub & TPS Routing",
                        value: "AFTER_SUB_ROUTING",
                      },
                    ],
                    error: !!errors.position,
                    helperText: errors.position ? "Position is required" : "",
                  },
                  {
                    type: "custom",
                    label: "Conditions",
                    highlightLabelStar: true,
                    component: (
                      <ConditionSenderFields
                        type="text"
                        conditionTypeOptions={[
                          { label: "CONTAINS", value: "CONTAINS" },
                          { label: "EQUAL_TO", value: "EQUAL_TO" },
                          { label: "IS_DIGIT", value: "IS_DIGIT" },
                          { label: "IS_NOT_DIGIT", value: "IS_NOT_DIGIT" },
                        ]}
                        conditionValues={dialogData.conditionValues || []}
                        handleConditionChange={handleConditionFieldChange}
                        handleAddCondition={handleAddCondition}
                        handleRemoveCondition={handleRemoveCondition}
                        errors={stringErrors}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    label: "Conditions",
                    highlightLabelStar: true,
                    component: (
                      <Box sx={{ width: "100%" }}>
                        <CustomCombinedField
                          labelValue1="Translated Sender ID"
                          placeholder1="Enter first value"
                          value1="Translated Sender ID"
                          editable1={true}
                          placeholder2={
                            isRandomMode ? "Numeric prefix" : "Value"
                          }
                          translatedSenderId={value2}
                          onTranslatedValueChange={(
                            value: React.SetStateAction<string>,
                          ) => {
                            const next =
                              typeof value === "function"
                                ? value(value2)
                                : value;
                            setValue2(
                              isRandomMode ? next.replace(/\D/g, "") : next,
                            );
                          }}
                          showLabel={true}
                          error2={
                            isRandomMode &&
                            value2.length > 0 &&
                            value2.length >= (dialogData.randomLength ?? 0)
                          }
                          helperText2={
                            isRandomMode &&
                            value2.length > 0 &&
                            value2.length >= (dialogData.randomLength ?? 0)
                              ? "Prefix must be shorter than the total length"
                              : undefined
                          }
                        />
                        {isRandomMode && value2 && (
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.75,
                              display: "block",
                              color: "text.secondary",
                            }}
                          >
                            Starts with: <strong>{value2}</strong>
                          </Typography>
                        )}
                      </Box>
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <Box sx={{ width: "100%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "flex-end" },
                          }}
                        >
                          <Tooltip
                            arrow
                            placement="top-start"
                            title={
                              isRandomSenderEnabled
                                ? ""
                                : "Generate random numeric SID instead of a fixed value."
                            }
                            slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isRandomSenderEnabled}
                                  sx={{
                                    color: COLORS.GOLD,
                                    "&.Mui-checked": { color: COLORS.GOLD },
                                  }}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setIsRandomSenderEnabled(checked);
                                    setDialogData((prev) => ({
                                      ...prev,
                                      cacheTtlSeconds: checked ? 60 : undefined,
                                      randomLength: checked ? 11 : undefined,
                                    }));
                                    if (checked) setValue2("");
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  Random numeric SID
                                </Typography>
                              }
                              sx={{
                                ml: 0,
                                mr: 0,
                                height: 40,
                                whiteSpace: "nowrap",
                              }}
                            />
                          </Tooltip>
                          {isRandomSenderEnabled && (
                            <>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 0.5, fontWeight: 500 }}
                                >
                                  New number every
                                </Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  placeholder="e.g. 60"
                                  value={dialogData.cacheTtlSeconds ?? ""}
                                  slotProps={{
                                    htmlInput: { min: 0 },
                                    input: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          sec
                                        </InputAdornment>
                                      ),
                                    },
                                  }}
                                  sx={randomFieldSx}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setDialogData((prev) => ({
                                      ...prev,
                                      cacheTtlSeconds:
                                        raw === "" ? undefined : Number(raw),
                                    }));
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 0.5, fontWeight: 500 }}
                                >
                                  Total Length
                                </Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  placeholder="e.g. 11"
                                  value={dialogData.randomLength ?? ""}
                                  slotProps={{
                                    htmlInput: { min: 0 },
                                    input: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          digits
                                        </InputAdornment>
                                      ),
                                    },
                                  }}
                                  sx={randomFieldSx}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setDialogData((prev) => ({
                                      ...prev,
                                      randomLength:
                                        raw === "" ? undefined : Number(raw),
                                    }));
                                  }}
                                />
                              </Box>
                            </>
                          )}
                        </Box>
                        {isRandomMode && (
                          <Box
                            sx={{
                              mt: 1.5,
                              px: 1.5,
                              py: 1,
                              display: "flex",
                              gap: 1,
                              alignItems: "flex-start",
                              borderRadius: "12px",
                              border: `1px solid ${COLORS.goldChipBorder}`,
                              backgroundColor: COLORS.goldButtonHoverBackground,
                            }}
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 18,
                                mt: "1px",
                                color: COLORS.GOLD,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", lineHeight: 1.6 }}
                            >
                              The server adds random digits to the prefix
                              above to reach{" "}
                              <strong>{dialogData.randomLength} digits</strong>.
                              The result is reused for{" "}
                              <strong>
                                {dialogData.cacheTtlSeconds}s (
                                {(
                                  (dialogData.cacheTtlSeconds ?? 0) / 60
                                ).toFixed(1)}{" "}
                                min)
                              </strong>
                              , then regenerated. Translated Sender ID must be
                              digits-only and shorter than the total length.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ),
                  },
                  {
                    type: "custom",
                    label: "Test",
                    highlightLabelStar: true,
                    component: (
                      <TestingSection
                        content={content}
                        setContent={setContent}
                        value={value}
                        setValue={setValue}
                        handleTestClick={handleTestClick}
                      />
                    ),
                  },
                ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                dialogMode !== "delete"
                  ? {
                      label: "Save",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        if (dialogMode === "edit") {
                          await editSenderTranslation(dialogData);
                        } else {
                          await addSenderTranslation(dialogData);
                        }
                      },
                      disabled:
                        isSaveDisabled ||
                        (dialogMode === "edit"
                          ? !hasJwtPermission("PUT_SENDER_TRANSLATION")
                          : !hasJwtPermission("POST_SENDER_TRANSLATION")),
                    }
                  : {
                      label: "Delete",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      textColor: COLORS.iconColor,
                      borderRadius: "12px",
                      onClick: deleteSenderTranslation,
                      disabled: !hasJwtPermission("DELETE_SENDER_TRANSLATION"),
                    },
              ]
        }
      />
    );
  },
);

export default SenderTranslationPage;
